import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { usePaperMode } from '../contexts/PaperModeContext';

const formatCurrency = (val) => {
  if (val == null) return '—';
  const num = parseFloat(val);
  return num >= 0 ? `$${num.toFixed(2)}` : `-$${Math.abs(num).toFixed(2)}`;
};

const formatPercent = (val) => {
  if (val == null) return '—';
  const num = parseFloat(val);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
};

const isClosingInstruction = (instr) => {
  if (!instr) return false;
  const i = String(instr).toUpperCase();
  return i.includes('CLOSE') || i === 'SELL' || i === 'BUY_TO_COVER';
};

const getActionLabel = (instr) => {
  if (!instr) return '—';
  const i = String(instr).toUpperCase();
  if (['BUY_TO_OPEN', 'SELL_TO_OPEN', 'SELL_SHORT'].includes(i)) return 'Open';
  if (['BUY_TO_CLOSE', 'SELL_TO_CLOSE', 'BUY_TO_COVER', 'SELL'].includes(i)) return 'Close';
  return i;
};

const getStrategyDisplay = (trade) => {
  if (trade.strategy) return trade.strategy;
  const instr = String(trade.instruction || '').toUpperCase();
  const side = (trade.put_call || trade.putCall) === 'P' ? 'Put' : 'Call';
  if (['BUY_TO_OPEN', 'SELL_TO_CLOSE'].includes(instr)) return `Long ${side}`;
  if (['SELL_TO_OPEN', 'BUY_TO_CLOSE'].includes(instr)) return `Short ${side}`;
  return side;
};

const PaperTrading = () => {
  const { paperMode } = usePaperMode();
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const closingTrades = trades.filter(t => isClosingInstruction(t.instruction));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [closeAllDialogOpen, setCloseAllDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, positionsRes, tradesRes] = await Promise.all([
        apiRequest('/api/paper-trading/summary'),
        apiRequest('/api/paper-trading/positions'),
        apiRequest('/api/paper-trading/trades?limit=200'),
      ]);

      if (summaryRes.data?.success) setSummary(summaryRes.data.data);
      if (positionsRes.data?.success) setPositions(positionsRes.data.data);
      if (tradesRes.data?.success) setTrades(tradesRes.data.data);
    } catch (err) {
      setError('Failed to fetch paper trading data');
      console.error('Error fetching paper trading data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReset = async () => {
    try {
      await apiRequest('/api/paper-trading/reset', { method: 'POST' });
      setResetDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to reset paper account');
    }
  };

  const handleCloseAll = async () => {
    try {
      await apiRequest('/api/paper-trading/close-all', { method: 'POST' });
      setCloseAllDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to close all positions');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Simulated trades using live Schwab quotes. No real orders are placed.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CloseIcon />}
            onClick={() => setCloseAllDialogOpen(true)}
            disabled={!positions.length}
          >
            Close All
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setResetDialogOpen(true)}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Total Realized P/L
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: summary.totalRealizedPL >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 'bold',
                  }}
                >
                  {formatCurrency(summary.totalRealizedPL)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Total Unrealized P/L
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: (summary.totalUnrealizedPL || 0) >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 'bold',
                  }}
                >
                  {formatCurrency(summary.totalUnrealizedPL)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mkt: {formatCurrency(summary.openMarketValue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Open Positions
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {summary.openCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cost: {formatCurrency(summary.openCost)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Win Rate
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {summary.winRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summary.totalClosed || 0} closed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Total Paper Trades
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {summary.totalTrades}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Open Positions */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Open Positions ({positions.length})
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {positions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No open paper positions.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Underlying</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Instruction</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Avg Price</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">Unrealized P/L</TableCell>
                    <TableCell align="right">P/L %</TableCell>
                    <TableCell>Expiration</TableCell>
                    <TableCell>Opened</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map((pos) => {
                    const mult = (pos.asset_type || pos.assetType) === 'EQUITY' ? 1 : 100;
                    const avgFill = parseFloat(pos.avg_fill_price || pos.avgFillPrice || 0);
                    const cost = avgFill * pos.quantity * mult;
                    const currentPrice = parseFloat(pos.current_price || pos.currentPrice || 0);
                    const unrealizedPL = parseFloat(pos.unrealized_pl || pos.unrealizedPl || 0);
                    const unrealizedPLPct = parseFloat(pos.unrealized_pl_percent || pos.unrealizedPlPercent || 0);
                    return (
                      <TableRow key={pos.id}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {pos.symbol}
                          </Typography>
                        </TableCell>
                        <TableCell>{pos.underlying || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={pos.asset_type || pos.assetType || 'OPTION'}
                            size="small"
                            color={pos.asset_type === 'EQUITY' ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={pos.instruction}
                            size="small"
                            color={pos.instruction.includes('BUY') ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{pos.quantity}</TableCell>
                        <TableCell align="right">${avgFill.toFixed(2)}</TableCell>
                        <TableCell align="right">{formatCurrency(cost)}</TableCell>
                        <TableCell align="right">{currentPrice > 0 ? `$${currentPrice.toFixed(2)}` : '—'}</TableCell>
                        <TableCell align="right">
                          <Typography
                            color={unrealizedPL >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {formatCurrency(unrealizedPL)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={unrealizedPLPct >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {formatPercent(unrealizedPLPct)}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(pos.expiration)}</TableCell>
                        <TableCell>{formatDateTime(pos.opened_at || pos.openedAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Closed Trades */}
      <Typography variant="h6" gutterBottom>
        Closed Trades ({closingTrades.length})
      </Typography>
      <Card>
        <CardContent>
          {closingTrades.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No closed paper trades yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Strategy</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Fill Price</TableCell>
                    <TableCell align="right">Realized P/L</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {closingTrades.map((trade) => (
                    <TableRow key={trade.id}>
                      <TableCell>{formatDateTime(trade.created_at || trade.createdAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {trade.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStrategyDisplay(trade)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getActionLabel(trade.instruction)}
                          size="small"
                          color={getActionLabel(trade.instruction) === 'Open' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{trade.quantity}</TableCell>
                      <TableCell align="right">${parseFloat(trade.fill_price || trade.fillPrice || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          color={parseFloat(trade.realized_pl || trade.realizedPl || 0) >= 0 ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {formatCurrency(trade.realized_pl || trade.realizedPl)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={trade.status}
                          size="small"
                          color={
                            trade.status === 'FILLED' ? 'success' :
                            trade.status === 'REJECTED' ? 'error' :
                            trade.status === 'SKIPPED' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{trade.reason || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Reset Confirmation */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Paper Account</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete ALL paper positions and trade history. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReset} variant="contained" color="error">
            Reset Everything
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close All Confirmation */}
      <Dialog open={closeAllDialogOpen} onClose={() => setCloseAllDialogOpen(false)}>
        <DialogTitle>Close All Paper Positions</DialogTitle>
        <DialogContent>
          <Typography>
            This will close all {positions.length} open paper positions at current market prices.
            Realized P/L will be calculated and recorded.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseAllDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCloseAll} variant="contained" color="warning">
            Close All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaperTrading;
